/* eslint-disable complexity */
import { useQuery } from '@apollo/client/react';
import { Termin } from '@/domain/termine/shared/types/Termin';
import { TerminWeekdayOccurrence } from '@/domain/termine/shared/types/TerminWeekdayOccurrence';
import { TerminWiederholungsIntervall } from '@/domain/termine/shared/types/TerminWiederholungsIntervall';
import { TerminWochentage } from '@/domain/termine/shared/types/TerminWochentage';
import { TerminWiederholung } from '@/domain/termine/shared/types/TerminWiederholung';
import { GET_TAGESDATEN_BY_STANDORTID_AND_ZEITRAUM } from '@/domain/termine/hooks/graphql/queries';
import { GetTagesDatenQuery } from '@/gql/graphql';
import { mapArbeitszeiten } from '@/domain/termine/shared/utils/mapArbeitszeiten';
import { mapSchichten } from '@/domain/termine/shared/utils/mapSchichten';

export function useTagesDaten(
  standortIds: string[],
  zeitraum: { von: string; bis: string },
) {
  const { data, error, loading } = useQuery<
    GetTagesDatenQuery,
    { standortIds: string[]; zeitraum: { von: string; bis: string } }
  >(GET_TAGESDATEN_BY_STANDORTID_AND_ZEITRAUM, {
    variables: { standortIds, zeitraum },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const displayData = loading && !data;

  if (!data) {
    return {
      termine: [],
      feiertage: [],
      kalender: [],
      error: error ?? null,
      loading: displayData,
    };
  }

  const kalender = data.termine_termineKalenderByStandortIdAndZeitraum;
  const feiertage = data.termine_feiertageByStandortIdsAndZeitraum;

  const termineResult = kalender.flatMap((kalender) => kalender.termine);

  const termine: Termin[] = termineResult.map(
    (item) =>
      new Termin({
        beginn: item.beginn ?? '',
        datum: item.datum,
        ende: item.ende ?? '',
        id: item.id,
        standortId: item.standortId,
        idTerminkalender: item.terminKalenderId ?? undefined,
        image: item.image
          ? {
              name: item.image.name ?? undefined,
              contentType: item.image.contentType ?? undefined,
              url: item.image.url ?? undefined,
              thumbnailUrl: item.image.thumbnailUrl ?? undefined,
            }
          : undefined,
        markierung: item.markierung,
        titel: item.titel,
        wiederholung: TerminWiederholung[item.wiederholung],
        individuelleWiederholung: item.individuelleWiederholung
          ? {
              ...item.individuelleWiederholung,
              wiederholungsIntervall:
                TerminWiederholungsIntervall[
                  item.individuelleWiederholung.wiederholungsIntervall
                ],
              wochentage: item.individuelleWiederholung.wochentage.map(
                (wochentag) => TerminWochentage[wochentag],
              ),
              weekDayOccurrence: item.individuelleWiederholung.weekDayOccurrence
                ? TerminWeekdayOccurrence[
                    item.individuelleWiederholung.weekDayOccurrence
                  ]
                : undefined,
            }
          : undefined,
        wiederholungBis: item.wiederholungBis ?? undefined,
        beschreibung: item.termineDetails?.beschreibung ?? '',
        specials: item.termineDetails?.specials ?? '',
        arbeitszeiten: mapArbeitszeiten(item.termine_arbeitszeiten),
        schichten: mapSchichten(item.termine_schichten),
        hasHiddenSchichten: item.hasHiddenSchichten ?? false,
        hasHiddenArbeitszeiten: item.hasHiddenArbeitszeiten ?? false,
      }),
  );

  return {
    termine,
    feiertage,
    kalender,
    loading: displayData,
    error: error ?? null,
  };
}
